import React from 'react';
import { useTranslation } from 'react-i18next';
import { DropdownSelect } from './DropdownSelect';
import { InputGroup } from './InputGroup';

interface FilterOption {
  value: string;
  label: string;
}

interface FilterJobBarProps {
  filters: {
    status: string;
    visibility: string;
    search: string;
    publication: string;
  };
  onFilterChange: (name: keyof FilterJobBarProps['filters'], value: string) => void;
}

const FilterJobBar: React.FC<FilterJobBarProps> = ({ filters, onFilterChange }) => {
  const { t } = useTranslation(['admin', 'common']);

  const visibilityOptions: FilterOption[] = [
    { value: '', label: t('all_types', { ns: 'common', defaultValue: 'Toutes les types' }) },
    {
      value: 'public',
      label: t('job_visibility_labels.public', { ns: 'jobs', defaultValue: 'Public' }),
    },
    {
      value: 'private',
      label: t('job_visibility_labels.private', { ns: 'jobs', defaultValue: 'Privé' }),
    },
  ];

  const statusOptions: FilterOption[] = [
    { value: '', label: t('all_statuses', { ns: 'common', defaultValue: 'Tous les statuts' }) },
    { value: 'open', label: t('job_status.open', { defaultValue: 'Ouvert' }) },
    { value: 'in_progress', label: t('job_status.in_progress', { defaultValue: 'En cours' }) },
    { value: 'completed', label: t('job_status.completed', { defaultValue: 'Terminé' }) },
    { value: 'cancelled', label: t('job_status.cancelled', { defaultValue: 'Annulé' }) },
    { value: 'paused', label: t('job_status.paused', { defaultValue: 'En pause' }) },
    { value: 'expired', label: t('job_status.expired', { defaultValue: 'Expirée' }) },
    {
      value: 'reviewing_quotes',
      label: t('job_status.reviewing_quotes', { defaultValue: 'En révision des soumissions' }),
    },
  ];

  const publicationOptions: FilterOption[] = [
    {
      value: '',
      label: t('jobModeration.allPublications', { ns: 'admin', defaultValue: 'All publications' }),
    },
    {
      value: 'draft',
      label: t('jobModeration.publication.draft', { ns: 'admin', defaultValue: 'Draft' }),
    },
    {
      value: 'published',
      label: t('jobModeration.publication.published', { ns: 'admin', defaultValue: 'Published' }),
    },
  ];

  return (
    <div className="flex flex-wrap gap-3 items-end justify-end px-4 py-3 border-b border-gray-300 bg-gray-50">
      <DropdownSelect<FilterOption>
        className="w-56"
        value={visibilityOptions.find(option => option.value === filters.visibility) ?? null}
        onChange={(_label, item) => onFilterChange('visibility', item?.value ?? '')}
        options={visibilityOptions}
        getOptionLabel={option => option.label}
        getOptionValue={option => option.value || 'all'}
        placeholder={t('all_types', { ns: 'common', defaultValue: 'Toutes les types' })}
      />

      <DropdownSelect<FilterOption>
        className="w-56"
        value={statusOptions.find(option => option.value === filters.status) ?? null}
        onChange={(_label, item) => onFilterChange('status', item?.value ?? '')}
        options={statusOptions}
        getOptionLabel={option => option.label}
        getOptionValue={option => option.value || 'all'}
        placeholder={t('all_statuses', { ns: 'common', defaultValue: 'Tous les statuts' })}
      />

      <DropdownSelect<FilterOption>
        className="w-56"
        value={publicationOptions.find(option => option.value === filters.publication) ?? null}
        onChange={(_label, item) => onFilterChange('publication', item?.value ?? '')}
        options={publicationOptions}
        getOptionLabel={option => option.label}
        getOptionValue={option => option.value || 'all'}
        placeholder={t('jobModeration.allPublications', { ns: 'admin' })}
      />

      <div className="w-56">
        <InputGroup
          value={filters.search}
          onChange={e => onFilterChange('search', e.target.value)}
          placeholder={t('admin:table_fields.title') + '...'}
        />
      </div>
    </div>
  );
};

export default FilterJobBar;
